import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskSplashScreenPageConfig } from '@front-platform/content-builder';
import { kioskSplashScreenPageResolveConfig } from '@front-platform/content-builder';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  computed,
  inject,
  input,
  viewChild,
  AfterViewInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import { LinkNavigationService } from '@core/services/link-navigation/link-navigation.service';
import {
  kioskSplashCtaOverlayClassList,
  shouldDisplayKioskLanguageFlags,
} from '@shared/blocks/kiosk/kiosk-select-language-block/kiosk-language-flags.util';
import {
  isPlayableVideoMedia,
  resolveKioskSplashBackgroundImageUrl,
} from '@shared/utils/media/kiosk-splash-background-media.util';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { Meta } from '@angular/platform-browser';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { PageHistoryService } from '@core/services/navigation-history/page-history.service';
import { MarketplaceOrderingUnavailableModalService } from '@core/services/marketplace-ordering-unavailable-modal/marketplace-ordering-unavailable-modal.service';
import { take } from 'rxjs';
export interface KioskSplashScreenPageRuntimeConfig {
  'body'?: any;
  'footer_kiosk'?: any;
  'title'?: string;
  'display_accessibility_button'?: boolean;
  'label_accessibility_button'?: string;
  'meta_description'?: string;
}

export type KioskSplashScreenPageConfigInput = Partial<
  Omit<KioskSplashScreenPageConfig, keyof KioskSplashScreenPageRuntimeConfig> &
    KioskSplashScreenPageRuntimeConfig
>;
export type KioskSplashScreenPageResolvedConfig = Omit<
  KioskSplashScreenPageConfig,
  keyof KioskSplashScreenPageRuntimeConfig
> &
  Partial<KioskSplashScreenPageRuntimeConfig>;
export interface KioskSplashScreenPageContract extends ConfigComponentContract<
  KioskSplashScreenPageConfigInput,
  KioskSplashScreenPageResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-splash-screen-page',
  templateUrl: './kiosk-splash-screen-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./kiosk-splash-screen-page.component.scss'],
  standalone: false,
})
export class KioskSplashScreenPageComponent
  implements OnInit, AfterViewInit, KioskSplashScreenPageContract
{
  readonly config = input<KioskSplashScreenPageConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskSplashScreenPageConfig,
    KioskSplashScreenPageRuntimeConfig
  >(kioskSplashScreenPageResolveConfig, () => ({ config: this.config() }));
  readonly body = computed(() => this.resolvedConfig()['body'] as unknown as any);
  readonly footerKiosk = computed(() => this.resolvedConfig()['footer_kiosk'] as unknown as any);

  readonly title = computed(() => this.resolvedConfig()['title'] as unknown as string);
  readonly showFooter = computed(() => this.resolvedConfig()['show_footer'] !== false);
  readonly labelCallToAction = computed(
    () => this.resolvedConfig()['label_call_to_action'] as unknown as string,
  );
  readonly linkUrl = computed(() => this.resolvedConfig()['link_url'] as unknown as string);
  readonly displayAccessibilityButton = computed(
    () => this.resolvedConfig()['display_accessibility_button'] as unknown as boolean,
  );
  readonly labelAccessibilityButton = computed(
    () => this.resolvedConfig()['label_accessibility_button'] as unknown as string,
  );
  readonly backgroundColor = computed(
    () =>
      this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor | undefined,
  );
  readonly backgroundImage = computed(
    () =>
      this.resolvedConfig()['background_image'] as unknown as IContentBuilderFieldImage | undefined,
  );
  readonly backgroundVideo = computed(
    () =>
      this.resolvedConfig()['background_video'] as unknown as IContentBuilderFieldImage | undefined,
  );
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor | undefined,
  );

  readonly backgroundOpacity = computed(
    () => this.resolvedConfig()['background_opacity'] as unknown as number | undefined,
  );

  readonly metaDescription = computed(
    () => this.resolvedConfig()['meta_description'] as unknown as string,
  );

  protected readonly isVideoMedia = computed(() => isPlayableVideoMedia(this.backgroundVideo()));

  protected readonly splashBackgroundImageUrl = computed(() =>
    resolveKioskSplashBackgroundImageUrl(this.backgroundVideo(), this.backgroundImage()),
  );

  private readonly marketplaceStoreSelector = inject(MarketplaceStoreSelector);

  protected readonly hasLanguageFlags = computed(() =>
    shouldDisplayKioskLanguageFlags(this.marketplaceStoreSelector.selectMarketplace().locales),
  );

  protected readonly ctaOverlayClassList = computed(() =>
    kioskSplashCtaOverlayClassList(this.hasLanguageFlags()),
  );

  bgVideo = viewChild<ElementRef<HTMLVideoElement>>('videoPlayer');

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationHistoryService: NavigationHistoryService,
    private el: ElementRef,
    private readonly meta: Meta,
    private readonly linkNavigationService: LinkNavigationService,
    private readonly authStoreSelector: AuthStoreSelector,
    private readonly analyticsService: AnalyticsService,
    private readonly pageHistoryService: PageHistoryService,
    private readonly marketplaceOrderingUnavailableModalService: MarketplaceOrderingUnavailableModalService,
  ) {
    this.authStoreSelector.selectUser$.pipe(takeUntilDestroyed()).subscribe(user => {
      if (user?.metafields && user.metafields.length) {
        const historyPages = this.pageHistoryService.getHistory();
        this.analyticsService.log({
          level: 'error',
          label: '#DOOD-1161 User connected on kiosk splash screen (must not happen) -',
          message: {
            user,
            route: window.location.href,
            historyPages,
            warn: 'make sure this splash screen is the home page',
          },
        });
      }
    });
  }

  ngAfterViewInit() {
    const video = this.bgVideo()?.nativeElement;
    if (!video) {
      return;
    }
    video.muted = true; // indispensable pour autoplay
    video.play().catch(err => {
      console.warn('Autoplay bloqué:', err);
    });
  }

  ngOnInit(): void {
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      '/' + this.activatedRoute.snapshot.url.join('/'),
      this.activatedRoute.snapshot,
    );

    if (this.textColor()?.value) {
      this.setTextOverrideColor();
    }
    const metaDescription = this.metaDescription();
    if (metaDescription) {
      this.meta.updateTag({
        name: 'description',
        content: metaDescription,
      });
    }
  }

  setTextOverrideColor(): void {
    const textColor = this.textColor()?.value;
    this.el.nativeElement.style.setProperty('--my-class-bg-color', textColor);
  }

  redirect(): void {
    const linkUrl = this.linkUrl();

    this.marketplaceOrderingUnavailableModalService
      .canProceedFromSplash$(linkUrl)
      .pipe(take(1))
      .subscribe(canProceed => {
        if (!canProceed) {
          return;
        }
        this.linkNavigationService.navigate(linkUrl);
      });
  }

  onInnerClick(event: Event): void {
    event.stopPropagation();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskSplashScreenPageComponent, { 'keyAliases': {} });
